/**
 * A player's clock.
 *
 * The server sends what each player had when the turn began and when it began; the
 * countdown is arithmetic from there, corrected by the offset between this browser's
 * clock and the server's. That means a client whose system clock is wrong shows the right
 * time, and no client can talk itself into believing it has more than it does — the
 * server decides the flag either way.
 */

import { useEffect, useState } from 'react';

import type { MatchState } from '@gok/shared';

/** Below this, show tenths: the last few seconds are the ones people watch. */
const PRECISE_BELOW_MS = 20_000;

export function formatClock(ms: number): string {
  const clamped = Math.max(0, ms);
  const minutes = Math.floor(clamped / 60_000);
  const seconds = (clamped % 60_000) / 1000;

  if (clamped < PRECISE_BELOW_MS) {
    return `${minutes}:${seconds.toFixed(1).padStart(4, '0')}`;
  }
  return `${minutes}:${Math.floor(seconds).toString().padStart(2, '0')}`;
}

/** Re-render on a timer, but only while something is actually counting down. */
function useTick(active: boolean, intervalMs: number): number {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const handle = setInterval(() => setTick((n) => n + 1), intervalMs);
    return () => clearInterval(handle);
  }, [active, intervalMs]);
  return Date.now();
}

export function Clock({
  match,
  player,
  clockOffset,
  running,
}: {
  match: MatchState;
  player: 0 | 1;
  clockOffset: number;
  running: boolean;
}) {
  const now = useTick(running, 100);
  const elapsed = running ? Math.max(0, now + clockOffset - match.turnStartedAt) : 0;
  // The first move from each side is untimed; until then the clock shows its full value.
  const counting = running && match.moves.length >= 2;
  const remaining = match.clocks[player] - (counting ? elapsed : 0);

  const low = remaining < 30_000;

  return (
    <div
      className={`tabular rounded-md px-2.5 py-1 font-mono text-lg leading-none font-medium
        ${running ? 'bg-raised text-ink' : 'text-muted'}
        ${low && running ? 'text-alert' : ''}`}
      aria-label={`${player === 0 ? 'First' : 'Second'} player clock`}
    >
      {formatClock(remaining)}
    </div>
  );
}
